/**
 * redactionProgressLedger — **l'avancement affiché d'un travail, retenu hors de
 * React** pour qu'il survive à la sortie de la conversation.
 *
 * ## Le défaut réparé
 *
 * La barre de `RedactionPlanProgress` est une rampe cosmétique : elle monte
 * d'un point toutes les 800 ms vers un plafond dicté par la phase serveur (voir
 * le docblock du composant). Cette valeur vivait dans un `React.useState` de la
 * carte, or la carte vit dans la frise du chat — quitter la conversation la
 * démonte et **détruit la valeur**. En revenant, l'avocat retrouvait sa
 * rédaction à 0 %, puis la voyait ramper de nouveau, alors que le travail, lui,
 * n'avait jamais cessé côté serveur.
 *
 * Ce n'est pas qu'un défaut cosmétique : lire « 7 % » sur un travail presque
 * fini pousse à relancer, ou à attendre pour rien. La barre ne mesure rien de
 * réel, mais elle est lue comme si elle mesurait quelque chose.
 *
 * ## Pourquoi un registre de module, et pas un store
 *
 * Aucun abonné n'a besoin d'être réveillé quand l'avancement change : la carte
 * tient déjà son propre `setInterval` et se re-rend toute seule. Elle n'a besoin
 * du registre qu'à **une** seconde précise — son montage, pour savoir d'où
 * repartir. Un store zustand ajouterait des notifications que personne n'écoute.
 *
 * ## Pourquoi retenir la valeur plutôt que l'instant de départ
 *
 * On pourrait croire la rampe recalculable depuis un horodatage de début. Elle
 * ne l'est pas : le plafond change au fil des phases, et la rampe *stagne* sous
 * son plafond avant de repartir. Deux travaux partis en même temps mais passés
 * en phase à des instants différents n'ont pas le même avancement. Seule la
 * valeur réellement servie porte cette histoire.
 *
 * ## Cycle de vie
 *
 * `reinitialiserLesAvancements` accompagne le changement de compte, pour la même
 * raison que le raisonnement (`redactionThinkingStore`) : ce qui appartenait à
 * l'avocat qui part ne doit pas survivre à son départ.
 */

/** L'avancement en pourcentage réellement servi, par travail. */
const avancementParJob = new Map<string, number>();

/**
 * D'où la carte doit repartir en se montant. `0` pour un travail inconnu — un
 * travail qui commence n'a rien à restaurer.
 */
export function lireAvancementServi(jobId: string): number {
  return avancementParJob.get(jobId) ?? 0;
}

/**
 * Retenir l'avancement servi.
 *
 * **Ne recule jamais** : la carte écrit à chaque tour d'horloge, et un montage
 * concurrent qui repartirait de 0 (frise re-rendue pendant que la précédente se
 * démonte) rendrait la barre sautillante. Une barre de progression qui redescend
 * se lit comme une panne.
 */
export function noterAvancementServi(jobId: string, valeur: number): void {
  const precedent = avancementParJob.get(jobId) ?? 0;
  if (valeur <= precedent) return;
  avancementParJob.set(jobId, valeur);
}

/** Tout oublier — changement de compte, et tests. */
export function reinitialiserLesAvancements(): void {
  avancementParJob.clear();
}
